import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import {
  ReaImportChange,
  ReaReviewParcelaDraft,
  ReaReviewReasonItem,
} from 'src/app/models/fincas/rea.model';

interface ReaReviewNavigationRequest {
  reasons: ReaReviewReasonItem[];
  fincaId: number | null;
  importId: string;
  changes: ReaImportChange[];
  onBeforeNavigate: () => void;
}

/**
 * Centralizes navigation decisions that are specific to the REA import modal.
 */
@Injectable()
export class ReaImportNavigationService {
  private readonly reviewQueryFlag = '1';

  constructor(private readonly router: Router) {}

  /** Opens the most specific destination available for the selected REA review reasons. */
  public openReviewParcelas(request: ReaReviewNavigationRequest): void {
    const parcelaId = this.findReviewParcelaId(request.reasons, request.changes);
    const draft = this.findReviewParcelaDraft(request.reasons);
    const codigoDgc = this.findReviewCodigoDgc(request.reasons);

    if (parcelaId !== null) {
      this.navigateToReviewParcela(parcelaId, request.importId, codigoDgc, request.onBeforeNavigate);
      return;
    }

    if (draft !== null || codigoDgc !== null) {
      const reviewDraft = draft ?? this.buildMinimalReviewDraft(codigoDgc, request.fincaId);

      this.navigateToReviewParcelaDraft(reviewDraft, request.fincaId, request.importId, codigoDgc, request.onBeforeNavigate);
      return;
    }

    if (request.fincaId !== null) {
      this.navigateToImportedFinca(request.fincaId, request.importId, true, codigoDgc, request.onBeforeNavigate);
    }
  }

  /** Opens the imported finca without enabling REA review mode. */
  public openImportedFinca(fincaId: number, onBeforeNavigate: () => void): void {
    this.navigateToImportedFinca(fincaId, '', false, null, onBeforeNavigate);
  }

  private navigateToReviewParcela(
    parcelaId: number,
    importId: string,
    codigoDgc: string | null,
    onBeforeNavigate: () => void,
  ): void {
    const queryParams = this.buildReviewQueryParams(importId, codigoDgc);

    onBeforeNavigate();

    this.router.navigate(['/dashboard', 'editar-parcela', parcelaId], { queryParams }).catch((error: unknown): void => {
      console.error('Unable to open REA review parcel', error);
    });
  }

  private navigateToReviewParcelaDraft(
    draft: ReaReviewParcelaDraft,
    fincaId: number | null,
    importId: string,
    codigoDgc: string | null,
    onBeforeNavigate: () => void,
  ): void {
    const queryParams = this.buildReviewQueryParams(importId, codigoDgc);
    const reviewModel = this.buildReviewParcelaModel(draft, fincaId, codigoDgc);

    queryParams['model'] = JSON.stringify(reviewModel);

    onBeforeNavigate();

    this.router.navigate(['/dashboard', 'crear-parcela'], { queryParams }).catch((error: unknown): void => {
      console.error('Unable to open omitted REA review parcel', error);
    });
  }

  private navigateToImportedFinca(
    fincaId: number,
    importId: string,
    reviewMode: boolean,
    codigoDgc: string | null,
    onBeforeNavigate: () => void,
  ): void {
    const queryParams = reviewMode ? this.buildReviewQueryParams(importId, codigoDgc) : {};
    const navigationExtras = Object.keys(queryParams).length > 0 ? { queryParams } : undefined;

    onBeforeNavigate();

    this.router.navigate(['/dashboard', 'editar-finca', fincaId], navigationExtras).catch((error: unknown): void => {
      console.error('Unable to open imported finca', error);
    });
  }

  private buildReviewQueryParams(importId: string, codigoDgc: string | null): Record<string, string> {
    const queryParams: Record<string, string> = { reaReview: this.reviewQueryFlag };

    if (importId !== '') {
      queryParams['reaImportId'] = importId;
    }

    if (codigoDgc !== null && codigoDgc.trim() !== '') {
      queryParams['reaCodigoDgc'] = codigoDgc.trim();
    }

    return queryParams;
  }

  private buildReviewParcelaModel(
    draft: ReaReviewParcelaDraft,
    fincaId: number | null,
    codigoDgc: string | null,
  ): ReaReviewParcelaDraft {
    const reviewModel: ReaReviewParcelaDraft = { ...draft };

    if (fincaId !== null && this.isEmptyReviewValue(reviewModel['id_finca'])) {
      reviewModel['id_finca'] = String(fincaId);
    }

    if (codigoDgc !== null && codigoDgc.trim() !== '' && this.isEmptyReviewValue(reviewModel['codigo_DGC'])) {
      reviewModel['codigo_DGC'] = codigoDgc.trim();
    }

    if (this.isEmptyReviewValue(reviewModel['notas'])) {
      reviewModel['notas'] = 'Parcela omitida en la importación REA. Revísala antes de guardarla.';
    }

    return reviewModel;
  }

  private buildMinimalReviewDraft(codigoDgc: string | null, fincaId: number | null): ReaReviewParcelaDraft {
    const draft: ReaReviewParcelaDraft = {
      nombre: 'Parcela pendiente de revisión REA',
      notas: 'Parcela omitida en la importación REA. Revísala antes de guardarla.',
      activo: '1',
      turbo: '1',
    };

    if (codigoDgc !== null) {
      draft['nombre'] = `Parcela REA ${codigoDgc}`;
      draft['codigo_DGC'] = codigoDgc;
    }

    if (fincaId !== null) {
      draft['id_finca'] = String(fincaId);
    }

    return draft;
  }

  private findReviewParcelaDraft(reasons: ReaReviewReasonItem[]): ReaReviewParcelaDraft | null {
    return reasons
      .map((reason) => reason.targetParcelaDraft)
      .find((draft): draft is ReaReviewParcelaDraft => this.isReviewParcelaDraft(draft)) ?? null;
  }

  private findReviewCodigoDgc(reasons: ReaReviewReasonItem[]): string | null {
    return reasons
      .map((reason) => reason.targetCodigoDgc?.trim() ?? '')
      .find((codigoDgc) => codigoDgc !== '') ?? null;
  }

  private findReviewParcelaId(reasons: ReaReviewReasonItem[], changes: ReaImportChange[]): number | null {
    const reasonTarget = reasons
      .map((reason) => this.toPositiveInteger(reason.targetParcelaId))
      .find((idParcela): idParcela is number => idParcela !== null);

    if (reasonTarget != null) {
      return reasonTarget;
    }

    const reviewKeys = new Set(reasons.map((reason) => reason.key));
    const directChangeTarget = changes
      .filter((change) => this.isReviewChange(change, reviewKeys))
      .map((change) => this.toPositiveInteger(change.idParcela))
      .find((idParcela): idParcela is number => idParcela !== null);

    return directChangeTarget ?? null;
  }

  private isReviewChange(change: ReaImportChange, reviewKeys: Set<string>): boolean {
    if (change.entity !== 'parcela') {
      return false;
    }

    if (change.action === 'not_in_rea' && reviewKeys.has('missing-in-rea')) {
      return true;
    }

    return this.fieldChangesForReview(change).some((fieldChange) => fieldChange.field === 'geometria')
      && reviewKeys.has('invalid-geometry');
  }

  private fieldChangesForReview(change: ReaImportChange): Array<{ field: string }> {
    return Array.isArray(change.fieldChanges) ? change.fieldChanges : [];
  }

  private isReviewParcelaDraft(value: ReaReviewParcelaDraft | null | undefined): value is ReaReviewParcelaDraft {
    return value !== null && value !== undefined && typeof value === 'object';
  }

  private isEmptyReviewValue(value: string | number | boolean | null | undefined): boolean {
    return value === null || value === undefined || String(value).trim() === '';
  }

  private toPositiveInteger(value: number | string | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }

    const numericValue = Number(value);

    return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : null;
  }
}
